import React from 'react'
import { useForm, usePage, Link } from '@inertiajs/react'
import Layout from '../../components/Layout'
import Breadcrumb from '../../components/Breadcrumb'
import PoloMapLibre from '../../components/maps/PoloMapLibre'
import ResponsibilitySection from '../../components/ResponsibilitySection'
import FormField from '../../components/forms/FormField'
import BooleanField from '../../components/forms/BooleanField'
import RadioGroupField from '../../components/forms/RadioGroupField'
import DateSelectField from '../../components/forms/DateSelectField'
import ErrorSummary from '../../components/forms/ErrorSummary'

function InfoItem({ label, value }) {
  let displayValue = value
  if (typeof value === 'boolean') {
    displayValue = value ? 'Sim' : 'Não'
  } else if (value === null || value === undefined || value === '') {
    displayValue = '-'
  }

  return (
    <li className="px-3 py-2 border border-gray-200 rounded mb-2">
      <span className="font-medium">{label}:</span>{' '}
      <span className="text-gray-700">{displayValue}</span>
    </li>
  )
}

function youtubeEmbedUrl(url) {
  if (!url) return null
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/)
  return match ? `https://www.youtube.com/embed/${match[1]}?rel=0` : null
}

export default function PoloShow({
  polo,
  responsibles = [],
  lotesResponsibles = [],
  revisors = [],
  possibleResponsibles = [],
  canEdit = false,
  canRevise = false,
  isAdmin = false
}) {
  const { errors } = usePage().props
  const task = canEdit ? 'input_data' : canRevise ? 'revise' : null

  const { data, setData, patch, processing } = useForm({
    task: task,
    polo: {
      // Text/date fields
      data_levantamento: polo.data_levantamento || '',
      video_url: polo.video_url || '',
      observacoes: polo.observacoes || '',
      // Boolean fields
      barreira_urbana: polo.barreira_urbana,
      continuidade_tracado: polo.continuidade_tracado,
      equipamento_urbano: polo.equipamento_urbano,
      terminal_de_transporte: polo.terminal_de_transporte,
      // Enum fields - use null, NOT empty string (PostgreSQL enums reject "")
      onibus: polo.onibus || null,
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/polos/${polo.id}`, { forceFormData: true })
  }

  const poloErrors = errors?.polo || errors || {}
  const embedUrl = youtubeEmbedUrl(polo.video_url)

  return (
    <Layout>
      <div className="py-8">
        <Breadcrumb items={[{ label: 'Polos', href: '/polos' }]} />

        <div className="grid lg:grid-cols-2 gap-6 mb-6">
          {/* Info Section */}
          <div className="card p-4">
            <h3 className="text-xl font-semibold text-brand-dark mb-2">{polo.nome}</h3>
            <h4 className="text-lg text-gray-600 mb-4">Informações Atuais</h4>
            {task ? (
              <form onSubmit={handleSubmit}>
                <ErrorSummary errors={errors?.polo || errors} />
                <ul className="space-y-1 mb-4">
                  <InfoItem label="Tipos" value={polo.tipos} />
                  <InfoItem label="Pontos" value={polo.pontos} />
                  <InfoItem label="Nível" value={polo.nivel} />
                  <InfoItem label="Ruídos sonoros" value={polo.ruidos_sonoros} />
                  <InfoItem label="Distinção do nome" value={polo.distincao_nome} />
                </ul>

                <DateSelectField
                  label="Data do levantamento"
                  name="polo[data_levantamento]"
                  value={data.polo.data_levantamento}
                  onChange={(e) => setData('polo', { ...data.polo, data_levantamento: e.target.value })}
                  error={poloErrors.data_levantamento}
                />

                <FormField
                  label="Endereço (url) para o vídeo no YouTube deste polo"
                  name="polo[video_url]"
                  value={data.polo.video_url}
                  onChange={(e) => setData('polo', { ...data.polo, video_url: e.target.value })}
                  error={poloErrors.video_url}
                />

                <BooleanField
                  label="Há barreiras urbanas em um raio de 200m do polo? (viadutos, ferrovias, pontes)"
                  name="polo[barreira_urbana]"
                  value={data.polo.barreira_urbana}
                  onChange={(val) => setData('polo', { ...data.polo, barreira_urbana: val })}
                  error={poloErrors.barreira_urbana}
                />

                <BooleanField
                  label="Há continuidade do traçado viário?"
                  name="polo[continuidade_tracado]"
                  value={data.polo.continuidade_tracado}
                  onChange={(val) => setData('polo', { ...data.polo, continuidade_tracado: val })}
                  error={poloErrors.continuidade_tracado}
                />

                <BooleanField
                  label="Há equipamentos urbanos em um raio de 200m do polo?"
                  name="polo[equipamento_urbano]"
                  value={data.polo.equipamento_urbano}
                  onChange={(val) => setData('polo', { ...data.polo, equipamento_urbano: val })}
                  error={poloErrors.equipamento_urbano}
                />

                <BooleanField
                  label="Há terminais de transporte em um raio de 500m do polo?"
                  name="polo[terminal_de_transporte]"
                  value={data.polo.terminal_de_transporte}
                  onChange={(val) => setData('polo', { ...data.polo, terminal_de_transporte: val })}
                  error={poloErrors.terminal_de_transporte}
                />

                <RadioGroupField
                  label="Há corredor ou faixa de ônibus?"
                  name="polo[onibus]"
                  options={{ 'Não': 'nao', 'Corredor': 'corredor', 'Faixa': 'faixa' }}
                  value={data.polo.onibus}
                  onChange={(val) => setData('polo', { ...data.polo, onibus: val })}
                  error={poloErrors.onibus}
                />

                <FormField
                  label="Percepções e observações"
                  name="polo[observacoes]"
                  value={data.polo.observacoes}
                  onChange={(e) => setData('polo', { ...data.polo, observacoes: e.target.value })}
                  as="textarea"
                  error={poloErrors.observacoes}
                />

                <button
                  type="submit"
                  disabled={processing}
                  className="mt-2 px-6 py-2 bg-brand-dark hover:bg-brand-light text-white font-medium rounded-md transition-colors disabled:opacity-50"
                >
                  {processing ? 'Atualizando...' : 'Atualizar'}
                </button>
              </form>
            ) : (
              <ul className="space-y-1">
                <InfoItem label="Tipos" value={polo.tipos} />
                <InfoItem label="Pontos" value={polo.pontos} />
                <InfoItem label="Nível" value={polo.nivel} />
                <InfoItem label="URL do vídeo" value={polo.video_url} />
                <InfoItem label="Data do levantamento" value={polo.data_levantamento} />
                <InfoItem label="Barreira urbana" value={polo.barreira_urbana} />
                <InfoItem label="Continuidade do traçado" value={polo.continuidade_tracado} />
                <InfoItem label="Equipamento urbano" value={polo.equipamento_urbano} />
                <InfoItem label="Terminal de transporte" value={polo.terminal_de_transporte} />
                <InfoItem label="Ônibus" value={polo.onibus} />
                <InfoItem label="Ruídos sonoros" value={polo.ruidos_sonoros} />
                <InfoItem label="Observações" value={polo.observacoes} />
                <InfoItem label="Distinção do nome" value={polo.distincao_nome} />
              </ul>
            )}
          </div>

          {/* Map Section */}
          <div className="card p-4">
            <h4 className="text-lg font-semibold mb-4">Mapa</h4>
            <PoloMapLibre poloId={polo.id} className="h-[500px]" />
          </div>
        </div>

        {/* Video Section */}
        {embedUrl && (
          <div className="card mb-6 p-4">
            <h4 className="text-lg font-semibold mb-4">Vídeo</h4>
            <div className="aspect-video max-w-2xl">
              <iframe
                src={embedUrl}
                className="w-full h-full rounded"
                allowFullScreen
              />
            </div>
          </div>
        )}

        {/* Responsibilities Section - Visible to all, editable by admin only */}
        <ResponsibilitySection
          polo={polo}
          responsibles={responsibles}
          lotesResponsibles={lotesResponsibles}
          revisors={revisors}
          possibleResponsibles={possibleResponsibles}
          isAdmin={isAdmin}
        />
      </div>
    </Layout>
  )
}
